/**
 * InvitationsSection.js — Self-contained invitations UI.
 *
 * Extracted from Settings.js so it can live on the Account page as its own
 * section. Gated on userRole >= "standard" by the caller (or internally if
 * userData is provided). Renders the "sent X of Y" header, Invite User button,
 * invitation table, and InviteModal.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { XCircle, RefreshCw } from "lucide-react";
import { meetsMinRole, getEffectiveInviteCap, ROLE_LABELS } from "../utils/roleUtils";
import {
  listInvitations,
  resendInvitation,
  revokeInvitation,
} from "../utils/invitationService";
import { showNotification } from "../utils/notificationSystem";
import InviteModal from "../utils/InviteModal";

const getStatusStyle = (status) => {
  switch (status) {
    case "pending":
      return {
        backgroundColor: "var(--color-warning-bg, #fefce8)",
        color: "var(--color-warning-text, #854d0e)",
      };
    case "accepted":
      return {
        backgroundColor: "var(--color-success-bg, #f0fdf4)",
        color: "var(--color-success-text, #166534)",
      };
    case "expired":
    case "revoked":
      return {
        backgroundColor: "var(--color-neutral-light, #f3f4f6)",
        color: "var(--color-neutral-dark, #374151)",
      };
    default:
      return {};
  }
};

const formatInviteDate = (timestamp) => {
  if (!timestamp) return "—";
  const date = timestamp._seconds
    ? new Date(timestamp._seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const InvitationsSection = ({ userData, userRole, onUserDataChange }) => {
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState("");
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [pendingActions, setPendingActions] = useState({});
  const activeActions = useRef(new Set());
  const [undeliveredInvite, setUndeliveredInvite] = useState(null);

  const canInvite = userRole && meetsMinRole(userRole, "standard");

  useEffect(() => {
    if (!canInvite) return;
    let cancelled = false;
    (async () => {
      setInvitationsLoading(true);
      const result = await listInvitations();
      if (!cancelled && result.success) setInvitations(result.invitations);
      if (!cancelled) setInvitationsError(result.success ? "" : (result.error || "Unable to load invitations"));
      if (!cancelled) setInvitationsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [canInvite, userData?.uid, userData?.companyId, userRole]);

  const refreshInvitations = useCallback(async () => {
    setInvitationsLoading(true);
    const result = await listInvitations();
    if (result.success) setInvitations(result.invitations);
    setInvitationsError(result.success ? "" : (result.error || "Unable to load invitations"));
    setInvitationsLoading(false);
  }, []);

  const beginAction = (id, action) => {
    if (activeActions.current.has(id)) return false;
    activeActions.current.add(id);
    setPendingActions((prev) => ({ ...prev, [id]: action }));
    setUndeliveredInvite((prev) => prev?.id === id ? null : prev);
    return true;
  };

  const finishAction = (id) => {
    activeActions.current.delete(id);
    setPendingActions((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleResend = async (inv) => {
    if (!beginAction(inv.id, "Resending...")) return;
    try {
      const result = await resendInvitation(inv.id);
      if (result.success) {
        if (result.emailSent) {
          showNotification("Invitation email resent", "info", 3000);
        } else {
          setUndeliveredInvite({ id: inv.id, email: inv.email, link: result.inviteLink });
          try {
            await navigator.clipboard.writeText(result.inviteLink);
            showNotification("Email was not delivered; new invite link copied", "warning", 4000);
          } catch {
            showNotification("Email was not delivered; copy the new invite link", "warning", 4000);
          }
        }
        await refreshInvitations();
      } else {
        showNotification(result.error || "Failed to resend", "error", 4000);
      }
    } catch {
      showNotification("Failed to resend invitation", "error", 4000);
    } finally {
      finishAction(inv.id);
    }
  };

  const handleRevoke = async (inv) => {
    if (!beginAction(inv.id, "Revoking...")) return;
    try {
      const result = await revokeInvitation(inv.id);
      if (result.success) {
        showNotification("Invitation revoked", "info", 3000);
        setInvitations((prev) =>
          prev.map((i) => (i.id === inv.id ? { ...i, status: "revoked" } : i))
        );
      } else {
        showNotification(result.error || "Failed to revoke", "error", 4000);
      }
    } catch {
      showNotification("Failed to revoke invitation", "error", 4000);
    } finally {
      finishAction(inv.id);
    }
  };

  const handleInviteCreated = async () => {
    await refreshInvitations();
    if (onUserDataChange) {
      onUserDataChange((prev) =>
        prev ? { ...prev, invitesSent: (prev.invitesSent || 0) + 1 } : prev
      );
    }
  };

  if (!canInvite) return null;

  const cap = getEffectiveInviteCap(userData);
  const sent = userData?.invitesSent || 0;

  return (
    <>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "12px",
      }}>
        <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>
          {cap === Infinity
            ? `You've sent ${sent} invitation${sent !== 1 ? 's' : ''} · Unlimited`
            : `You've sent ${sent} of ${cap} invitation${cap !== 1 ? 's' : ''}`}
        </span>
        <button
          className="button-confirm"
          onClick={() => setIsInviteModalOpen(true)}
          disabled={cap - sent <= 0}
          style={{ fontSize: "var(--text-sm)", padding: "6px 16px" }}
        >
          Invite User
        </button>
      </div>

      {invitationsLoading && invitations.length === 0 ? (
        <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", padding: "12px 0" }}>
          Loading invitations...
        </div>
      ) : invitationsError ? (
        <div role="alert" style={{ padding: "12px 0", color: "var(--color-error-text)" }}>
          <p>Could not load invitations: {invitationsError}</p>
          <button type="button" className="button-secondary" disabled={invitationsLoading} onClick={refreshInvitations}>Retry invitations</button>
        </div>
      ) : invitations.length === 0 ? (
        <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", padding: "12px 0" }}>
          No invitations sent yet.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--color-light-border, #d1d5db)", textAlign: "left" }}>
              <th style={{ padding: "8px 12px", fontWeight: 600 }}>Email</th>
              <th style={{ padding: "8px 12px", fontWeight: 600 }}>Role</th>
              <th style={{ padding: "8px 12px", fontWeight: 600 }}>Status</th>
              <th style={{ padding: "8px 12px", fontWeight: 600 }}>Sent</th>
              <th style={{ padding: "8px 12px", fontWeight: 600, textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => (
              <tr key={inv.id} style={{ borderBottom: "1px solid var(--color-light-border, #d1d5db)" }}>
                <td style={{ padding: "8px 12px" }}>{inv.email}</td>
                <td style={{ padding: "8px 12px" }}>{ROLE_LABELS[inv.role] || inv.role}</td>
                <td style={{ padding: "8px 12px" }}>
                  <span style={{
                    ...getStatusStyle(inv.status),
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "var(--text-xs)",
                    fontWeight: 500,
                  }}>
                    {inv.status}
                  </span>
                </td>
                <td style={{ padding: "8px 12px", color: "var(--color-text-secondary)" }}>
                  {formatInviteDate(inv.createdAt)}
                </td>
                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                  <div style={{ display: "flex", justifyContent: "center", gap: "4px" }}>
                    {(inv.status === "pending" || inv.status === "expired") && (
                      <Tooltip title="Resend invitation email" arrow>
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Resend invitation email"
                            disabled={Boolean(pendingActions[inv.id])}
                            onClick={() => handleResend(inv)}
                            sx={{ color: "var(--color-text-secondary)" }}
                          >
                            <RefreshCw size={14} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {inv.status === "pending" && (
                      <Tooltip title="Revoke invitation" arrow>
                        <span>
                          <IconButton size="small" aria-label="Revoke invitation" disabled={Boolean(pendingActions[inv.id])} onClick={() => handleRevoke(inv)} sx={{ color: "var(--color-error-text)" }}>
                            <XCircle size={14} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  {pendingActions[inv.id] && <span role="status">{pendingActions[inv.id]}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {undeliveredInvite && (
        <div role="alert" style={{ padding: "12px", marginTop: "12px", background: "var(--color-warning-bg, #fefce8)" }}>
          <p>The email to {undeliveredInvite.email} was not delivered. Copy this new invitation link to share it directly. The previous link no longer works.</p>
          <input aria-label="New invitation link" readOnly value={undeliveredInvite.link || ""} style={{ width: "100%" }} />
          <button type="button" onClick={() => setUndeliveredInvite(null)}>Dismiss</button>
        </div>
      )}

      <InviteModal
        isOpen={isInviteModalOpen}
        setIsOpen={setIsInviteModalOpen}
        userData={userData}
        onInviteCreated={handleInviteCreated}
      />
    </>
  );
};

export default InvitationsSection;
